// Summary-card (cheat-sheet) review surface for a question set. Renders every
// `item_kind === "cheat"` item grouped by its bulk's topic, with the summary
// prose (markdown + LaTeX), key formulas, methods and pitfalls. Each topic gets
// a "Go deeper" control (toggle + depth select + note) whose state is seeded
// from `cheatsheet_marks` and autosaved (debounced) via PATCH .../cheatsheet-marks.
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateCheatsheetMarks } from "@/api/question-sets";
import type {
  CheatsheetMarks,
  CheatsheetTopicMark,
  QuestionSetRead,
  QuestionSetItemRead,
} from "@/api/library-types";
import type { LearnerTopicStat, MistakeTopic } from "@/api/stats-types";
import { MathText, RawMath } from "@/components/math-text";
import { MarkdownLite } from "@/projects/markdown-lite";
import { TopicStatChips } from "./set-detail";

interface KeyFormula {
  label?: string;
  expression?: string;
  note?: string;
}
interface KeyMethod {
  name?: string;
  steps?: string[];
  when_to_use?: string;
}

function str(c: Record<string, unknown>, k: string): string {
  return typeof c[k] === "string" ? (c[k] as string) : "";
}
function strList(c: Record<string, unknown>, k: string): string[] {
  const v = c[k];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}
function objList<T>(c: Record<string, unknown>, k: string): T[] {
  const v = c[k];
  return Array.isArray(v) ? (v as T[]) : [];
}

/** One topic's cheat items + the resolved label/slug. */
interface TopicGroup {
  slug: string;
  label: string;
  items: QuestionSetItemRead[];
}

function groupCheatsByTopic(data: QuestionSetRead): TopicGroup[] {
  const byId = new Map(data.items.map((it) => [it.id, it]));
  const groups: TopicGroup[] = [];
  for (const b of [...data.bulks].sort((a, b) => a.order - b.order)) {
    const items = b.sequence
      .map((id) => byId.get(id))
      .filter((it): it is QuestionSetItemRead => !!it && it.item_kind === "cheat");
    if (items.length === 0) continue;
    // Prefer the item's own topic_slug/label when present, else the bulk's.
    const first = items[0].content;
    groups.push({
      slug: str(first, "topic_slug") || b.topic_slug,
      label: str(first, "topic_label") || b.topic_label,
      items,
    });
  }
  return groups;
}

export function CheatsheetCards({
  data,
  topicStatBySlug,
  mistakeBySlug,
}: {
  data: QuestionSetRead;
  topicStatBySlug?: Map<string, LearnerTopicStat>;
  mistakeBySlug?: Map<string, MistakeTopic>;
}) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupCheatsByTopic(data), [data]);

  // Local marks state, seeded from the server payload. Keyed by topic_slug.
  const [marks, setMarks] = useState<Record<string, CheatsheetTopicMark>>(
    () => ({ ...(data.cheatsheet_marks?.topics ?? {}) }),
  );

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const save = useCallback(
    (next: Record<string, CheatsheetTopicMark>) => {
      if (timer.current) clearTimeout(timer.current);
      const payload: CheatsheetMarks = { topics: next };
      timer.current = setTimeout(() => {
        void updateCheatsheetMarks(data.id, payload).catch(() => {});
      }, 600);
    },
    [data.id],
  );

  const update = useCallback(
    (group: TopicGroup, patch: Partial<CheatsheetTopicMark>) => {
      setMarks((prev) => {
        const cur = prev[group.slug] ?? { label: group.label, depth: "dig_deeper" };
        const next = {
          ...prev,
          [group.slug]: { label: group.label, ...cur, ...patch },
        };
        save(next);
        return next;
      });
    },
    [save],
  );

  if (groups.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">
        {t("Summary cards")}
      </h2>
      <p className="mt-1 text-sm text-ink-faint">
        {t("One card per topic. Toggle “Go deeper” on anything you want a follow-up set to expand.")}
      </p>
      <div className="mt-3 space-y-3">
        {groups.map((g) => {
          const mark = marks[g.slug];
          const marked = !!mark?.marked;
          const depth = mark?.depth ?? "dig_deeper";
          return (
            <div key={g.slug} className="card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-ink">{g.label}</h3>
                  <TopicStatChips
                    stat={topicStatBySlug?.get(g.slug)}
                    mistake={mistakeBySlug?.get(g.slug)}
                  />
                </div>
                <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm font-medium text-ink-soft">
                  <input
                    type="checkbox"
                    checked={marked}
                    onChange={(e) => update(g, { marked: e.target.checked })}
                    className="h-4 w-4 rounded border-black/20 text-brand-600 focus:ring-brand-500"
                  />
                  {t("Go deeper")}
                </label>
              </div>

              {marked && (
                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-brand-50 p-3">
                  <label className="flex items-center gap-2 text-sm text-ink-soft">
                    {t("How deep?")}
                    <select
                      value={depth}
                      onChange={(e) => update(g, { depth: e.target.value })}
                      className="rounded-lg border border-black/10 bg-white px-2 py-1 text-sm"
                    >
                      <option value="dig_deeper">{t("Go deeper")}</option>
                      <option value="from_scratch">{t("From scratch")}</option>
                    </select>
                  </label>
                  <input
                    value={mark?.note ?? ""}
                    onChange={(e) => update(g, { note: e.target.value })}
                    placeholder={t("Note for the follow-up (optional)")}
                    className="min-w-0 flex-1 rounded-lg border border-black/10 px-3 py-1.5 text-sm"
                  />
                </div>
              )}

              <div className="mt-3 space-y-4 border-t border-black/5 pt-3">
                {g.items.map((it) => (
                  <CheatItem key={it.id} content={it.content} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CheatItem({ content }: { content: Record<string, unknown> }) {
  const { t } = useTranslation();
  const summary = str(content, "summary");
  const formulas = objList<KeyFormula>(content, "key_formulas");
  const methods = objList<KeyMethod>(content, "key_methods");
  const pitfalls = strList(content, "common_pitfalls");

  return (
    <div className="space-y-3">
      {summary && (
        <div className="prose-sm text-ink">
          <MarkdownLite text={summary} />
        </div>
      )}

      {formulas.length > 0 && (
        <div>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Key formulas")}
          </h4>
          <div className="space-y-1.5">
            {formulas.map((f, i) => (
              <div key={i} className="rounded-lg bg-black/5 px-3 py-2 text-sm">
                {f.label && <span className="font-medium text-ink">{f.label}: </span>}
                {f.expression && <RawMath text={f.expression} />}
                {f.note && <p className="mt-0.5 text-xs text-ink-faint"><MathText text={f.note} /></p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {methods.length > 0 && (
        <div>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Key methods")}
          </h4>
          <div className="space-y-2">
            {methods.map((m, i) => (
              <div key={i} className="text-sm">
                {m.name && <p className="font-medium text-ink"><MathText text={m.name} /></p>}
                {Array.isArray(m.steps) && m.steps.length > 0 && (
                  <ol className="ml-4 list-decimal space-y-0.5 text-ink-soft">
                    {m.steps.map((s, j) => (
                      <li key={j}><MathText text={s} /></li>
                    ))}
                  </ol>
                )}
                {m.when_to_use && (
                  <p className="mt-0.5 text-xs text-ink-faint">
                    <span className="font-medium">{t("When:")} </span>
                    <MathText text={m.when_to_use} />
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {pitfalls.length > 0 && (
        <div>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Common pitfalls")}
          </h4>
          <ul className="ml-4 list-disc space-y-0.5 text-sm text-ink-soft">
            {pitfalls.map((p, i) => (
              <li key={i}><MathText text={p} /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
